/**
 * They said no.
 *
 * The one thing this window exists to capture is *why*. A row that simply
 * disappears off the board teaches nobody anything: six weeks later somebody
 * finds the same company in an import, rings them, and is told again — more
 * sharply — what they were told the first time.
 *
 * So the note is required and the window says so plainly rather than
 * disabling the button and leaving somebody to work out what is missing. The
 * reason chips are there to make the common case one click; the note is there
 * because the reason chip has never once been enough on its own.
 *
 * Deliberately not a delete. The row stays, out of every board and every
 * count, listed on its own tab with the reason attached. Losing the record
 * and losing the reason are the same mistake.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import Sheet, { Group, Field } from '../Sheet.jsx';

/*
 * Two ways a lead comes off the board, and the same sheet for both.
 *
 * "Not interested" is a decision they made. "No response" is one
 * nobody made — nobody could reach them. The rows read differently on
 * the report and are worked differently afterwards, so each has its own
 * button, its own reasons and its own count; the form behind them is
 * the same three questions, so it is one component rather than two that
 * drift apart.
 *
 * The lists are the server's, which rejects anything else — see
 * DROP_REASONS in routes/ops.js, and do not let the two drift.
 */
const MODES = {
  no: {
    title: 'Not interested',
    save: 'Mark not interested',
    first: 'Not interested',
    tone: 'g-red',
    icon: 'x',
    hero: 'It comes off the pipeline. Nobody will chase it, and it stops counting as open work.',
    ask: 'What they actually said',
    hint: 'Said they signed with Zoho last month. Asked us not to call again.',
    reasons: ['Not interested', 'Too expensive', 'Chose a competitor', 'No budget',
      'Not the right fit', 'Already using something else', 'Other']
  },
  quiet: {
    title: 'No response',
    save: 'Mark no response',
    first: 'No response',
    tone: 'g-amber',
    icon: 'eyeoff',
    hero: 'It comes off the pipeline as nobody we could reach. Reopen it any time they answer.',
    ask: 'What you tried',
    hint: 'Called three times over two weeks and emailed twice. Number rings out.',
    reasons: ['No response', 'Wrong contact', 'Not reachable', 'Number not working', 'Other']
  }
};

export default function NotInterestedSheet({ id, onClose, mode = 'no' }) {
  const { crm, dropDeal } = useApp();
  const deal = crm.deals.find(d => d.id === id);
  const M = MODES[mode] || MODES.no;

  const [reason, setReason] = useState(M.first);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(false);

  if (!deal) return null;
  const lead = deal.kind === 'lead';
  const short = note.trim().length < 3;

  const save = async () => {
    /* Asked for once, then shown. The alternative — a button that is dead
       until the right thing is typed — makes somebody guess which field it
       is waiting on. */
    if (short) { setShown(true); return; }
    setBusy(true);
    const ok = await dropDeal(id, { reason, note: note.trim(), kind: mode });
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <Sheet
      icon={M.icon}
      tone={M.tone}
      title={lead ? M.title : 'Close this deal off'}
      onClose={onClose}
      onSave={save}
      saveLabel={busy ? 'Saving…' : M.save}
      bodyClass="ni-form"
      hero={(
        <div className="ni-hero">
          <b>{deal.title}</b>
          <span>
            {lead ? M.hero : 'It comes off the deal board and out of the forecast.'}
          </span>
        </div>
      )}
    >
      <Group>Why</Group>

      {/* The chip that is on wears the mode's colour, not the red one:
          a red pill against "nobody answered" reads as a verdict. */}
      <div className={`ni-reasons ${mode}`}>
        {M.reasons.map(r => (
          <button
            key={r}
            type="button"
            className={`ni-chip${reason === r ? ' on' : ''}`}
            onClick={() => setReason(r)}
          >
            {reason === r ? <Icon name="check" /> : null}{r}
          </button>
        ))}
      </div>

      <Field label={M.ask} wide>
        <textarea
          rows={4}
          autoFocus
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder={M.hint}
        />
        {/* The one line that stops this becoming a row of identical chips. */}
        <small className={`ni-hint${shown && short ? ' bad' : ''}`}>
          {shown && short
            ? 'Write a line first — this is all the next person will have.'
            : 'Required. Six weeks from now this is the only thing anybody will have to go on.'}
        </small>
      </Field>
    </Sheet>
  );
}
